import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import {
  FLEX_DRAFT_CONFIRMATION_DICTIONARY,
  FLEX_DRAFT_INFO_COPY_DICTIONARY,
  FLEX_DRAFT_SCHEDULE_BI_WEEKLY,
  FLEX_DRAFT_SCHEDULE_FORM_BI_WEEKLY_DICTIONARY,
  FLEX_DRAFT_SCHEDULE_FORM_SEMI_MONTHLY_DICTIONARY,
} from './constants';
import { initialState } from './reducer';
import {
  FLEX_DRAFT_PAYMENT_STATUSES,
  FLEX_DRAFT_STATUS_UPDATES_DICTIONARY,
} from './statusDictionary';
import { selectStatusDataProp as selectAutopayStatusDataProp } from '../Autopay/selectors';
import {
  getProfileCurrentSetting,
  selectCustomerServicePhoneNumber,
} from '../../Main/selectors';
import makeSelectMobile from '../../Mobile/selectors';
import {
  PAYMENT_SOURCES_FIRST_ACCOUNT,
  PAYMENT_SOURCES_SECOND_ACCOUNT,
} from '../Sources/constants';
import { getMaskedValue } from '../Sources/helpers';
import { initialState as sourcesInitialState } from '../Sources/reducer';
import makeSelectPaymentSources from '../Sources/selectors';
import {
  convertNumToCurrencyStr,
  convertToNonBreakingPhoneNumber,
  formatDate,
  interpolate,
} from '../../../utils/globalHelpers';

/**
 * Selector for the whole Flex Draft state domain
 */
const selectFlexDraftDomain = (state) => state.flexDraft || initialState;

/**
 * Selector for Flex Draft state's main properties
 */
const makeSelectFlexDraft = (prop) => createSelector(
  selectFlexDraftDomain,
  (state) => state[prop],
);

/**
 * @description Selects values within the allowedSetting object if present
 */
const selectAllowedSetting = (prop) => createSelector(
  selectStatusDataProp('allowedSetting'),
  (allowedSetting) => allowedSetting ? allowedSetting[prop] : null,
);

/**
 * @description Selects data utilized by Flex Draft edit amount drawer on desktop
 */
const selectAmountDrawerProps = () => createSelector(
  selectSelectedAdditionalPrincipal(),
  selectFormErrors('amount'),
  selectStatusDataProp('allowedSetting'),
  (
    additionalPrincipal,
    amountFormErrors,
    {
      maximumAllowedAdditionalPrincipal,
      nextPaymentDueDate,
      regularMonthlyPayment,
    },
  ) => ({
    additionalPrincipal,
    amountFormErrors,
    dueDate: formatDate(nextPaymentDueDate, 'UTC', 'LL'),
    maxAdditionalPrincipal: maximumAllowedAdditionalPrincipal,
    regularMonthlyPayment: convertNumToCurrencyStr(regularMonthlyPayment),
  }),
);

/**
 * @description Formats data for enable/change flex draft request
 */
const selectChangeRequestBody = () => createSelector(
  makeSelectPaymentSources('accountInfoNew'),
  makeSelectFlexDraft('forms'),
  (
    {
      [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountInfoNew,
      [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountInfoNew,
    },
    {
      amount: { additionalPrincipal },
      scheduleBiWeekly,
      scheduleSelect: { draftSchedule },
      scheduleSemiMonthly,
    },
  ) => {
    let firstAccount = {};
    let secondAccount = {};

    // Use first account's new and already validated unmasked values if set
    if (!isEmpty(firstAccountInfoNew)) {
      const {
        accountNumber,
        accountType,
        nameOnAccount,
        routingNumber,
      } = firstAccountInfoNew;

      firstAccount = {
        accountNumber,
        accountType,
        nameOnAccount,
        routingNumber,
      };
    }

    // Use second account's new and already validated unmasked values if set
    if (!isEmpty(secondAccountInfoNew)) {
      const {
        accountNumber,
        accountType,
        nameOnAccount,
        routingNumber,
      } = secondAccountInfoNew;

      secondAccount = {
        accountNumber,
        accountType,
        nameOnAccount,
        routingNumber,
      };
    }

    return {
      formData: {
        additionalPrincipal,
        draftSchedule,
        scheduleBiWeekly,
        scheduleSemiMonthly,
      },
      sourceData: { firstAccount, secondAccount },
    };
  },
);

const selectConfirmationData = () => createSelector(
  makeSelectFlexDraft('confirmationNumber'),
  selectForm('scheduleSelect'),
  selectPaymentAmount(),
  makeSelectPaymentSources('accountInfoNew'),
  makeSelectPaymentSources('useAccountInfoNew'),
  (
    confirmationNumber,
    { draftSchedule },
    amountRowValue,
    {
      [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountInfoNew,
      [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountInfoNew,
    },
    { [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountUseInfoNew },
  ) => {
    if (!confirmationNumber) {
      return false;
    }

    let firstAccountNumber = { label: '', value: '' };
    let firstRoutingNumber = { label: '', value: '' };
    let secondAccountNumber = { label: '', value: '' };
    let secondRoutingNumber = { label: '', value: '' };
    const isScheduleBiWeekly = draftSchedule === FLEX_DRAFT_SCHEDULE_BI_WEEKLY;

    const { accountNumber: firstAccount, routingNumber: firstRouting } = firstAccountInfoNew;
    firstAccountNumber = {
      isNotBold: true,
      label: 'Account Number',
      value: `******${firstAccount.slice(-4)}`,
    };
    firstRoutingNumber = {
      isNotBold: true,
      label: 'Routing Number',
      value: `******${firstRouting.slice(-4)}`,
    };

    if (secondAccountUseInfoNew) {
      const { accountNumber: secondAccount, routingNumber: secondRouting } = secondAccountInfoNew;
      secondAccountNumber = {
        isNotBold: true,
        label: 'Account Number',
        value: `******${secondAccount.slice(-4)}`,
      };
      secondRoutingNumber = {
        isNotBold: true,
        label: 'Routing Number',
        value: `******${secondRouting.slice(-4)}`,
      };
    }

    const paymentSourceData = secondAccountUseInfoNew ? [
      { label: 'Payment Source 1', value: '' },
      { ...firstRoutingNumber },
      { ...firstAccountNumber },
      { label: 'Payment Source 2', value: '' },
      { ...secondRoutingNumber },
      { ...secondAccountNumber },
    ] : [
      { ...firstRoutingNumber, isNotBold: false },
      { ...firstAccountNumber, isNotBold: false },
    ];

    return [
      { label: 'Confirmation Number', value: confirmationNumber },
      { label: 'Payment Amount', value: amountRowValue },
      ...paymentSourceData,
      {
        label: 'Payment Schedule',
        value: isScheduleBiWeekly ? 'Bi-Weekly' : 'Semi-Monthly',
      },
    ];
  },
);

/**
 * @description formats confirmation for confirmation component
 */
const selectConfirmationDictionary = () => createSelector(
  selectIsFlexDraftEnabledOrPending(),
  (isFlexDraftEnabledOrPending) => {
    const { change, enable, shared } = FLEX_DRAFT_CONFIRMATION_DICTIONARY;
    return {
      ...isFlexDraftEnabledOrPending ? change : enable,
      ...shared,
    };
  },
);

/**
 * @description formats dictionary for disclosure component
 */
const selectDisclosureDictionary = () => createSelector(
  selectSummaryScheduledDateString(),
  selectPaymentAmount(),
  (summarySubText, summaryText) => ({
    headerText: 'Terms & Conditions',
    summaryButtonLabel: 'Accept Terms',
    summaryHelperText: 'Scroll to the bottom of the Terms and Conditions to proceed.',
    summarySubText,
    summaryText,
  }),
);

/**
 * @description selects form data via prop
 */
const selectForm = (prop) => createSelector(
  makeSelectFlexDraft('forms'),
  (forms) => forms[prop],
);

/**
 * @description selects form error data via prop
 */
const selectFormErrors = (prop) => createSelector(
  makeSelectFlexDraft('formErrors'),
  (formErrors) => formErrors[prop],
);

/**
 * @description formats infosection data (array) for confirmation component on desktop
 */
const selectInfosectionData = () => createSelector(
  selectForm('scheduleSelect'),
  selectIsFlexDraftEnabledOrPending(),
  ({ draftSchedule }, isFlexDraftEnabledOrPending) => {
    const infoSectionDictionary = isFlexDraftEnabledOrPending ?
      FLEX_DRAFT_INFO_COPY_DICTIONARY.confirmation.change :
      FLEX_DRAFT_INFO_COPY_DICTIONARY.confirmation.enable;

    return infoSectionDictionary[draftSchedule];
  },
);

/**
 * @description formats infosection dictionary for confirmation component on mobile
 */
const selectInfosectionDictionary = () => createSelector(
  selectIsFlexDraftEnabledOrPending(),
  (isFlexDraftEnabledOrPending) => isFlexDraftEnabledOrPending ?
    FLEX_DRAFT_INFO_COPY_DICTIONARY.confirmation.change :
    FLEX_DRAFT_INFO_COPY_DICTIONARY.confirmation.enable,
);

const selectIsEligibleForFlexDraft = () => createSelector(
  makeSelectFlexDraft('statusData'),
  ({ allowedOperation }) => allowedOperation === 'Request_Enable',
);

/**
 * @description selects if flex draft is enabled or pending change
 */
const selectIsFlexDraftEnabledOrPending = () => createSelector(
  makeSelectFlexDraft('statusData'),
  ({ allowedOperation, status }) =>
    status === 'Change_Request_Processing' ||
    (allowedOperation === 'Request_Change' && status !== 'Deleted'),
);

/**
 * @description derives autopay payment amount
 */
const selectPaymentAmount = () => createSelector(
  selectSelectedAdditionalPrincipal(),
  selectStatusDataProp('allowedSetting'),
  (selectedAdditionalPrincipal, { regularMonthlyPayment = 0 }) =>
    convertNumToCurrencyStr(regularMonthlyPayment + selectedAdditionalPrincipal),
);

/**
 * @description selects and formats payment due date
 */
const selectPaymentDueDate = () => createSelector(
  selectAllowedSetting('nextPaymentDueDate'),
  (nextPaymentDueDate) => nextPaymentDueDate ? formatDate(nextPaymentDueDate, 'UTC', 'LL') : '',
);

const selectRequestStatus = (prop) => createSelector(
  makeSelectFlexDraft('requestStatus'),
  (requestStatus) => requestStatus[prop],
);

/**
 * @description Selects data utilized by edit schedule drawer on desktop
 */
const selectScheduleDrawerProps = () => createSelector(
  makeSelectFlexDraft('forms'),
  makeSelectFlexDraft('selectedDraftScheduleStep'),
  ({
    scheduleBiWeekly,
    scheduleSelect,
    scheduleSemiMonthly,
  },
  selectedDraftScheduleStep) => ({
    formValues:
      { scheduleBiWeekly, scheduleSelect, scheduleSemiMonthly },
    selectedDraftScheduleStep,
  }),
);

/**
 * @description selects appropriate additional principal value to use
 */
const selectSelectedAdditionalPrincipal = () => createSelector(
  selectForm('amount'),
  ({ additionalPrincipal: formAdditionalPrincipal }) => {
    if (formAdditionalPrincipal || formAdditionalPrincipal === 0) {
      return formAdditionalPrincipal;
    }
    return 0;
  },
);

/**
 * @description selectes Flex Draft review screen data
 */
const selectSetUpFlexDraftReviewData = () => createSelector(
  selectPaymentAmount(),
  selectSetUpPayFromRowProps(),
  selectSetUpPaymentScheduleRowProps(),
  selectForm('scheduleSelect'),
  selectForm('scheduleBiWeekly'),
  selectForm('scheduleSemiMonthly'),
  (
    amountRowValue,
    { paymentSourceRowValue },
    { paymentScheduleRowValue },
    { draftSchedule },
    { startingDay },
    { firstDraft, secondDraft },
  ) => {
    let scheduledPayments;
    let scheduledPaymentsLabel;
    let startDate;

    if (draftSchedule === FLEX_DRAFT_SCHEDULE_BI_WEEKLY) {
      ({ display: startDate } = FLEX_DRAFT_SCHEDULE_FORM_BI_WEEKLY_DICTIONARY
        .startingDay.menuItems.find(({ value }) => value === startingDay));
      scheduledPayments = `${startDate} of the month`;
      scheduledPaymentsLabel = 'Start Date';
    } else {
      const { ordinalNumber: firstOrdinal } = FLEX_DRAFT_SCHEDULE_FORM_SEMI_MONTHLY_DICTIONARY
        .firstDraft.menuItems.find(({ value }) => value === firstDraft);
      const { ordinalNumber: secondOrdinal } = FLEX_DRAFT_SCHEDULE_FORM_SEMI_MONTHLY_DICTIONARY
        .secondDraft.menuItems.find(({ value }) => value === secondDraft);
      scheduledPayments =
          `${firstOrdinal} and ${secondOrdinal}`;
      scheduledPaymentsLabel = 'Payment Days';
    }

    return [{
      label: 'Payment Amount',
      value: amountRowValue,
    }, {
      label: 'Payment Account',
      value: paymentSourceRowValue,
    }, {
      label: 'Payment Schedule',
      value: paymentScheduleRowValue,
    }, {
      label: scheduledPaymentsLabel,
      value: scheduledPayments,
    }];
  },
);

/**
 * @description selects Pay From row button properties
 */
const selectSetUpPayFromRowProps = () => createSelector(
  makeSelectPaymentSources('accountInfoExisting'),
  makeSelectPaymentSources('accountInfoNew'),
  makeSelectPaymentSources('useAccountInfoExisting'),
  makeSelectPaymentSources('useAccountInfoNew'),
  (
    {
      flexdraft: {
        [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountInfoExisting,
        [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountInfoExisting,
      },
    },
    {
      [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountInfoNew,
      [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountInfoNew,
    },
    {
      flexdraft: {
        [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountUseInfoExisting,
        [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountUseInfoExisting,
      },
    },
    {
      [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountUseInfoNew,
      [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountUseInfoNew,
    },
  ) => {
    let noPaymentSource = true;
    let numberOfAccounts = 0;

    if (
      (firstAccountUseInfoExisting && !isEmpty(firstAccountInfoExisting)) ||
      (firstAccountUseInfoNew && !isEmpty(firstAccountInfoNew))
    ) {
      noPaymentSource = false;
      numberOfAccounts += 1;
    }

    if (
      (secondAccountUseInfoExisting && !isEmpty(secondAccountInfoExisting)) ||
      (secondAccountUseInfoNew && !isEmpty(secondAccountInfoNew))
    ) {
      noPaymentSource = false;
      numberOfAccounts += 1;
    }

    return {
      noPaymentSource,
      paymentSourceErrorLabel: 'Add Payment Information',
      paymentSourceRowValue: `${numberOfAccounts} Account${numberOfAccounts === 1 ? '' : 's'}`,
    };
  },
);

/**
 * @description selects Payment Schedule row button properties
 */
const selectSetUpPaymentScheduleRowProps = () => createSelector(
  selectForm('scheduleSelect'),
  selectForm('scheduleBiWeekly'),
  selectForm('scheduleSemiMonthly'),
  (
    { draftSchedule },
    { startingDay, startingMonth },
    { firstDraft, secondDraft },
  ) => {
    let paymentScheduleRowSubText = '';
    const isScheduleBiWeekly = draftSchedule === FLEX_DRAFT_SCHEDULE_BI_WEEKLY;
    const noPaymentSchedule = isScheduleBiWeekly
      ? (isEmpty(startingDay) || isEmpty(startingMonth))
      : (isEmpty(firstDraft) || isEmpty(secondDraft));

    // Only provide payment schedule subtext if a schedule has been set up
    if (!noPaymentSchedule) {
      if (isScheduleBiWeekly) {
        paymentScheduleRowSubText = 'Payments will be made every 14 days after start date';
      } else {
        const { ordinalNumber: firstOrdinal } = FLEX_DRAFT_SCHEDULE_FORM_SEMI_MONTHLY_DICTIONARY
          .firstDraft.menuItems.find(({ value }) => value === firstDraft);
        const { ordinalNumber: secondOrdinal } = FLEX_DRAFT_SCHEDULE_FORM_SEMI_MONTHLY_DICTIONARY
          .secondDraft.menuItems.find(({ value }) => value === secondDraft);
        paymentScheduleRowSubText =
          `Payments will be made on the ${firstOrdinal} and ${secondOrdinal} of each month`;
      }
    }

    return {
      noPaymentSchedule,
      paymentScheduleErrorLabel: 'Select Schedule',
      paymentScheduleRowSubText,
      paymentScheduleRowValue: isScheduleBiWeekly ? 'Bi-Weekly' : 'Semi-Monthly',
    };
  },
);

/**
 * @description selects all Set Up screen row button properties
 */
const selectSetUpRowButtonsProps = () => createSelector(
  selectPaymentAmount(),
  selectSetUpPayFromRowProps(),
  selectSetUpPaymentScheduleRowProps(),
  (
    amountRowValue,
    {
      noPaymentSource,
      paymentSourceErrorLabel,
      paymentSourceRowValue,
    },
    {
      noPaymentSchedule,
      paymentScheduleErrorLabel,
      paymentScheduleRowSubText,
      paymentScheduleRowValue,
    },
  ) => ({
    amountRowValue,
    noPaymentSchedule,
    noPaymentSource,
    paymentScheduleErrorLabel,
    paymentScheduleRowSubText,
    paymentScheduleRowValue,
    paymentSourceErrorLabel,
    paymentSourceRowValue,
  }),
);

/**
 * @description determines if payment source(s) validate button should be rendered
 */
const selectShouldRenderValidateButton = () => createSelector(
  makeSelectPaymentSources('useAccountInfoExisting'),
  makeSelectPaymentSources('useAccountInfoNew'),
  makeSelectFlexDraft('isSecondAccountFormEnabled'),
  (
    { flexdraft: useAccountInfoExisting },
    useAccountInfoNew,
    isSecondAccountFormEnabled,
  ) =>
    (
      !useAccountInfoExisting[PAYMENT_SOURCES_FIRST_ACCOUNT] &&
      !useAccountInfoNew[PAYMENT_SOURCES_FIRST_ACCOUNT]
    ) || (
      !useAccountInfoExisting[PAYMENT_SOURCES_SECOND_ACCOUNT] &&
      !useAccountInfoNew[PAYMENT_SOURCES_SECOND_ACCOUNT] &&
      isSecondAccountFormEnabled
    ),
);

/**
 * @description selects form state for payment source accounts utilized by flex draft
 */
const selectSourceFormState = () => createSelector(
  makeSelectPaymentSources('accountInfoExisting'),
  makeSelectPaymentSources('accountInfoNew'),
  makeSelectPaymentSources('formErrors'),
  makeSelectPaymentSources('formValues'),
  makeSelectPaymentSources('useAccountInfoExisting'),
  makeSelectPaymentSources('useAccountInfoNew'),
  makeSelectPaymentSources('activeAccountForm'),
  (
    {
      flexdraft: {
        [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountInfoExisting,
        [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountInfoExisting,
      },
    },
    {
      [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountInfoNew,
      [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountInfoNew,
    },
    formErrors,
    formValues,
    {
      flexdraft: {
        [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountUseInfoExisting,
        [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountUseInfoExisting,
      },
    },
    {
      [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountUseInfoNew,
      [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountUseInfoNew,
    },
    activeAccountForm,
  ) => {
    let firstAccountFormErrors;
    let firstAccountFormValues;
    let secondAccountFormErrors;
    let secondAccountFormValues;

    // Use first account's existing values when flag is set and info is available
    if (firstAccountUseInfoExisting && !isEmpty(firstAccountInfoExisting)) {
      const {
        accountType,
        maskedAccountNumber,
        maskedNameOnAccount,
        maskedRoutingNumber,
      } = firstAccountInfoExisting;

      firstAccountFormValues = {
        accountNumber: maskedAccountNumber,
        accountNumberConfirm: maskedAccountNumber,
        accountType,
        nameOnAccount: maskedNameOnAccount,
        routingNumber: maskedRoutingNumber,
      };
      firstAccountFormErrors = sourcesInitialState.formErrors[PAYMENT_SOURCES_FIRST_ACCOUNT];
    }

    // Use seconds account's existing values when flag is set and info is available
    if (secondAccountUseInfoExisting && !isEmpty(secondAccountInfoExisting)) {
      const {
        accountType,
        maskedAccountNumber,
        maskedNameOnAccount,
        maskedRoutingNumber,
      } = secondAccountInfoExisting;

      secondAccountFormValues = {
        accountNumber: maskedAccountNumber,
        accountNumberConfirm: maskedAccountNumber,
        accountType,
        nameOnAccount: maskedNameOnAccount,
        routingNumber: maskedRoutingNumber,
      };
      secondAccountFormErrors = sourcesInitialState.formErrors[PAYMENT_SOURCES_SECOND_ACCOUNT];
    }

    // Use first account's new and already validated values when flag is set and info is available
    if (
      firstAccountUseInfoNew &&
      !isEmpty(firstAccountInfoNew) &&
      isEmpty(firstAccountFormValues)
    ) {
      const {
        accountNumber,
        accountType,
        nameOnAccount,
        routingNumber,
      } = firstAccountInfoNew;

      firstAccountFormValues = {
        accountNumber: getMaskedValue(accountNumber),
        accountNumberConfirm: getMaskedValue(accountNumber),
        accountType,
        nameOnAccount: getMaskedValue(nameOnAccount),
        routingNumber: getMaskedValue(routingNumber),
      };
      firstAccountFormErrors = sourcesInitialState.formErrors[PAYMENT_SOURCES_FIRST_ACCOUNT];
    }

    // Use second account's new and already validated values when flag is set and info is available
    if (
      secondAccountUseInfoNew &&
      !isEmpty(secondAccountInfoNew) &&
      isEmpty(secondAccountFormValues)
    ) {
      const {
        accountNumber,
        accountType,
        nameOnAccount,
        routingNumber,
      } = secondAccountInfoNew;

      secondAccountFormValues = {
        accountNumber: getMaskedValue(accountNumber),
        accountNumberConfirm: getMaskedValue(accountNumber),
        accountType,
        nameOnAccount: getMaskedValue(nameOnAccount),
        routingNumber: getMaskedValue(routingNumber),
      };
      secondAccountFormErrors = sourcesInitialState.formErrors[PAYMENT_SOURCES_SECOND_ACCOUNT];
    }

    // If no values set for first account, set to current unvalidated form values
    if (isEmpty(firstAccountFormValues)) {
      firstAccountFormValues = formValues[PAYMENT_SOURCES_FIRST_ACCOUNT];
      firstAccountFormErrors = formErrors[PAYMENT_SOURCES_FIRST_ACCOUNT];
    }

    // If no values set for second account, set to current unvalidated form values
    if (isEmpty(secondAccountFormValues)) {
      secondAccountFormValues = formValues[PAYMENT_SOURCES_SECOND_ACCOUNT];
      secondAccountFormErrors = formErrors[PAYMENT_SOURCES_SECOND_ACCOUNT];
    }

    return {
      activeAccountForm,
      formErrors: {
        [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountFormErrors,
        [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountFormErrors,
      },
      formValues: {
        [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountFormValues,
        [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountFormValues,
      },
    };
  },
);

/**
 * @description selects statusData value via prop
 */
const selectStatusDataProp = (prop) => createSelector(
  makeSelectFlexDraft('statusData'),
  (statusData) => statusData[prop],
);

/**
 * @description selects status block message props
 */
const selectStatusMessageProps = () => createSelector(
  makeSelectFlexDraft('statusData'),
  selectAutopayStatusDataProp('status'),
  selectIsFlexDraftEnabledOrPending(),
  selectCustomerServicePhoneNumber(),
  makeSelectMobile('isMobile'),
  (
    { status },
    autopayStatus,
    isFlexDraftEnabledOrPending,
    customerServicePhoneNumber,
    isMobile,
  ) => {
    if (!status ||
      autopayStatus === 'Enabled' ||
      !isFlexDraftEnabledOrPending
    ) return null;

    let props;
    if (isMobile) {
      props = FLEX_DRAFT_PAYMENT_STATUSES.mobile[status];
    } else {
      props = FLEX_DRAFT_PAYMENT_STATUSES.desktop[status];
    }

    const formattedPhoneNumber = convertToNonBreakingPhoneNumber(customerServicePhoneNumber);
    const { secondaryMessage } = props;
    const formattedMessage = interpolate(
      secondaryMessage,
      { customerServicePhoneNumber: formattedPhoneNumber },
    );

    if (isMobile) {
      const {
        button,
        header,
        iconAlignSelf,
        iconType,
        message,
      } = props;
      return {
        button,
        header,
        iconAlignSelf,
        iconType,
        message,
        secondaryMessage: formattedMessage,
      };
    }

    const {
      header,
      iconAlignSelf,
      iconName,
      linkLabel,
      message,
      operation,
    } = props;
    return {
      header,
      iconAlignSelf,
      iconName,
      linkLabel,
      operation,
      secondaryMessage: formattedMessage,
      text: message,
    };
  },
);

/**
 * @description selects status updates banner props
 */
const selectStatusUpdatesMessageProps = () => createSelector(
  getProfileCurrentSetting(),
  makeSelectFlexDraft('statusData'),
  selectAutopayStatusDataProp('status'),
  (
    { lastLoginDate },
    { allowedOperation, statement, status },
    autopayStatus,
  ) => {
    let messageProps;

    if ([
      'Change_Additional_Information_Required',
      'Deleted',
      'Enable_Additional_Information_Required',
      'Rejected',
    ].includes(status)) {
      if (moment(lastLoginDate) > (moment(statement.sentAt).add(2, 'days'))) {
        return { text: '', variant: '' };
      }
    }

    if (autopayStatus === 'Enabled') {
      messageProps = status === 'Rejected' || status === 'Enable_Additional_Information_Required' ?
        FLEX_DRAFT_STATUS_UPDATES_DICTIONARY.autopayEnrolled[status][allowedOperation] :
        FLEX_DRAFT_STATUS_UPDATES_DICTIONARY.autopayEnrolled[status];
    } else {
      messageProps = status === 'Rejected' || status === 'Enable_Additional_Information_Required' ?
        FLEX_DRAFT_STATUS_UPDATES_DICTIONARY.autopayNotEnrolled[status][allowedOperation] :
        FLEX_DRAFT_STATUS_UPDATES_DICTIONARY.autopayNotEnrolled[status];
    }

    return messageProps || { text: '', variant: '' };
  },
);

/**
 * @description formats string to be displayed in flex draft payment summary component
 */
const selectSummaryScheduledDateString = () => createSelector(
  selectPaymentDueDate(),
  selectForm('scheduleSelect'),
  selectForm('scheduleBiWeekly'),
  selectForm('scheduleSemiMonthly'),
  (
    nextPaymentDueDate,
    { draftSchedule },
    { startingDay, startingMonth },
    { firstDraft, secondDraft },
  ) => {
    let startDay;
    const isScheduleBiWeekly = draftSchedule === FLEX_DRAFT_SCHEDULE_BI_WEEKLY;
    const noPaymentSchedule = isScheduleBiWeekly
      ? (isEmpty(startingDay) || isEmpty(startingMonth))
      : (isEmpty(firstDraft) || isEmpty(secondDraft));

    if (!noPaymentSchedule) {
      if (isScheduleBiWeekly) {
        ({ display: startDay } = FLEX_DRAFT_SCHEDULE_FORM_BI_WEEKLY_DICTIONARY
          .startingDay.menuItems.find(({ value }) => value === startingDay));
      } else {
        ({ ordinalNumber: startDay } = FLEX_DRAFT_SCHEDULE_FORM_SEMI_MONTHLY_DICTIONARY
          .firstDraft.menuItems.find(({ value }) => value === firstDraft));
      }

      return `Start on the ${startDay} day of the month`;
    }

    return `Due on ${nextPaymentDueDate}`;
  },
);

/**
 * @description selects whether the user can navigate into Flex Draft
 */
const selectValidateFlexdraftNavigation = () => createSelector(
  makeSelectFlexDraft('statusData'),
  ({ allowedOperation }) => allowedOperation !== 'None',
);

export default makeSelectFlexDraft;
export {
  selectAllowedSetting,
  selectAmountDrawerProps,
  selectConfirmationData,
  selectConfirmationDictionary,
  selectChangeRequestBody,
  selectDisclosureDictionary,
  selectForm,
  selectFormErrors,
  selectInfosectionData,
  selectInfosectionDictionary,
  selectIsEligibleForFlexDraft,
  selectIsFlexDraftEnabledOrPending,
  selectPaymentDueDate,
  selectRequestStatus,
  selectScheduleDrawerProps,
  selectSelectedAdditionalPrincipal,
  selectSetUpFlexDraftReviewData,
  selectSetUpRowButtonsProps,
  selectShouldRenderValidateButton,
  selectSourceFormState,
  selectStatusDataProp,
  selectStatusMessageProps,
  selectStatusUpdatesMessageProps,
  selectSummaryScheduledDateString,
  selectValidateFlexdraftNavigation,
};
